"use client"

import Text from "@/components/common/Typography/Text"
import Title from "@/components/common/Typography/Title"
import { Button, Card, Col, Popover, Row } from "antd"
import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

type LandingHotelProps = {
  hotels?: any[]
}

const toRem = (px: number) => `${(px / 9).toFixed(3)}rem`

const formatPrice = (value: any) => {
  if (value === null || value === undefined) return "—"
  const numeric = Number(value)
  if (Number.isNaN(numeric)) return "—"
  return new Intl.NumberFormat("vi-VN").format(numeric)
}

const getAllImages = (pictures: any) => {
  if (!pictures || typeof pictures !== "object") return null

  const allImages: string[] = []
  for (const key in pictures) {
    if (Array.isArray(pictures[key])) {
      allImages.push(...pictures[key])
    }
  }

  if (allImages.length === 0) return null
  return allImages
}

const stableHash = (input: string) => {
  let hash = 0
  for (let i = 0; i < input.length; i += 1) {
    hash = (hash << 5) - hash + input.charCodeAt(i)
    hash |= 0
  }
  return Math.abs(hash)
}

const getLastTwoDaysOfNextMonth = () => {
  const now = new Date()
  const year = now.getFullYear()
  const month = now.getMonth() + 1

  const lastDayDate = new Date(year, month + 1, 0)
  const lastDay = lastDayDate.getDate()
  const secondLastDay = lastDay - 1

  const formatDate = (day: number) => {
    return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
  }

  return {
    in: formatDate(secondLastDay),
    out: formatDate(lastDay),
  }
}

const pickStableImage = (hotel: any) => {
  const images = getAllImages(hotel?.pictures)
  if (!images || images.length === 0) return null

  const seed =
    String(hotel?.id ?? "") ||
    String(hotel?.hotelId ?? "") ||
    String(hotel?.hotelName ?? hotel?.translatedName ?? "")
  const idx = stableHash(seed) % images.length
  return images[idx]
}

const normalizeImageUrl = (value?: string) => {
  if (!value) return ""
  const trimmed = String(value).trim()
  if (!trimmed) return ""
  if (trimmed.startsWith("http://")) return `https://${trimmed.slice("http://".length)}`
  if (trimmed.startsWith("//")) return `https:${trimmed}`
  return trimmed
}

const getHotelAddressLine = (hotel: any) => {
  const parts = [
    hotel?.address,
    hotel?.street,
    hotel?.ward,
    hotel?.district,
    hotel?.city,
    hotel?.cityName,
    hotel?.province,
    hotel?.provinceName,
    hotel?.country,
    hotel?.countryName,
  ]
    .map((v) => (v ? String(v).trim() : ""))
    .filter(Boolean)
  if (parts.length === 0) return ""
  return Array.from(new Set(parts)).join(", ")
}

const HotelCard = ({ hotel, mode }: { hotel: any; mode: "compact" | "expanded" }) => {
  const { i18n } = useTranslation()
  const imageUrl = pickStableImage(hotel)
  const normalizedImageUrl = imageUrl ? normalizeImageUrl(imageUrl) : ""
  const name = hotel?.hotelName || hotel?.translatedName || "Khách sạn"
  const hotelSearchId = hotel?.searchHotelId || hotel?.hotelSearchId || hotel?.hotelId || ""
  const reviewsText = hotel?.numberOfReviews ? `(${hotel?.numberOfReviews} đánh giá)` : ""
  const priceText = `${formatPrice(hotel?.basePrice)} ${hotel?.setCurrency || "VND"}/đêm`
  const addressLine = getHotelAddressLine(hotel)
  const hasImage = Boolean(imageUrl && normalizedImageUrl)

  const handleCardClick = () => {
    const { in: checkIn, out: checkOut } = getLastTwoDaysOfNextMonth()
    const hotelId = hotel?.hotelId
    const query = new URLSearchParams({
      adt: "2",
      child: "0",
      room: "1",
      in: checkIn,
      out: checkOut,
      loc: hotelId,
      type: "HOTEL",
    }).toString()
    window.open(`/${i18n.language}/hotels/result/detail/${hotelId}/${hotelSearchId}?${query}&renew=true`, '_blank')
  }

  return (
    <Card
      style={{ backgroundColor: "#ecece6", borderRadius: toRem(16) }}
      hoverable
      className={mode === "expanded" ? "landing-hotel__card landing-hotel__card--expanded" : "landing-hotel__card"}
      onClick={mode === "compact" ? handleCardClick : undefined}
    >
      <div
        className={mode === "expanded" ? "landing-hotel__media landing-hotel__media--expanded" : "landing-hotel__media"}
        style={{
          marginBottom: toRem(12),
          overflow: "hidden",
          borderRadius: toRem(4),
          backgroundColor: "#ffffff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {hasImage ? (
          <img
            src={normalizedImageUrl}
            alt={name}
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
            loading={mode === "expanded" ? "eager" : "lazy"}
            onError={(e) => {
              e.currentTarget.style.display = "none"
            }}
          />
        ) : null}
      </div>

      <div className={mode === "expanded" ? "landing-hotel__content landing-hotel__content--expanded" : "landing-hotel__content"}>
        <div className={mode === "expanded" ? "landing-hotel__name landing-hotel__name--expanded" : "landing-hotel__name"} title={name}>
          {name}
        </div>

        <div className={mode === "expanded" ? "landing-hotel__meta landing-hotel__meta--expanded" : "landing-hotel__meta"}>
          <Text className="te-text-small">⭐ {hotel?.starRating} sao</Text>
          {reviewsText ? (
            <Text
              title={reviewsText}
              className={mode === "expanded" ? "landing-hotel__reviews landing-hotel__reviews--expanded" : "landing-hotel__reviews"}
              type="secondary"
              style={{ display: "block" }}
            >
              {reviewsText}
            </Text>
          ) : undefined}
          {/* {mode === "expanded" && addressLine ? (
            <Text title={addressLine} type="secondary" className="te-text-small" style={{ display: "block" }}>
              {addressLine}
            </Text>
          ) : undefined} */}
        </div>

        <Text className="landing-hotel__price" title={priceText} type="danger" strong>
          {priceText}
        </Text>
      </div>
    </Card>
  )
}

const getHotelKey = (hotel: any, idx: number) =>
  String(hotel?.id ?? hotel?.hotelId ?? hotel?.hotelName ?? hotel?.translatedName ?? idx)

const LandingHotel = ({ hotels = [] }: LandingHotelProps) => {
  const normalizedHotels = Array.isArray(hotels) ? hotels.slice(0, 10) : []
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const openPopoverIdsRef = useRef<Set<string>>(new Set())
  const [isAnyPopoverOpen, setIsAnyPopoverOpen] = useState(false)

  const canSlide = normalizedHotels.length > 0
  const autoDelayMs = 4000
  const shouldBlockScroll = () => openPopoverIdsRef.current.size > 0

  const scrollNext = () => {
    if (shouldBlockScroll()) return
    const el = scrollRef.current
    if (!el) return

    const nearEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 4
    if (nearEnd) {
      el.scrollTo({ left: 0, behavior: "smooth" })
      return
    }

    el.scrollBy({ left: el.clientWidth, behavior: "smooth" })
  }

  const scrollPrev = () => {
    if (shouldBlockScroll()) return
    const el = scrollRef.current
    if (!el) return
    el.scrollBy({ left: -el.clientWidth, behavior: "smooth" })
  }

  const handlePopoverOpenChange = (id: string, open: boolean) => {
    const set = openPopoverIdsRef.current
    if (open) set.add(id)
    else set.delete(id)
    setIsAnyPopoverOpen(set.size > 0)
  }

  useEffect(() => {
    if (!canSlide || isAnyPopoverOpen) return
    const id = window.setInterval(scrollNext, autoDelayMs)
    return () => window.clearInterval(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canSlide, isAnyPopoverOpen])

  return (
    <Row className="landing-hotel" gutter={[16, 16]}>
      <Col span={24}>
        <Title level={2} style={{ margin: 0 }}>
          Khách sạn gợi ý
        </Title>
      </Col>

      <Col span={24}>
        {normalizedHotels.length > 0 ? (
          <div className="landing-hotel__slider">
            <Button
              onClick={scrollPrev}
              aria-label="prev"
              className="landing-hotel__nav landing-hotel__nav--prev"
              shape="circle"
              disabled={isAnyPopoverOpen}
            >
              ‹
            </Button>
            <Button
              onClick={scrollNext}
              aria-label="next"
              className="landing-hotel__nav landing-hotel__nav--next"
              shape="circle"
              disabled={isAnyPopoverOpen}
            >
              ›
            </Button>

            <div
              ref={scrollRef}
              className="landing-hotel__track"
              style={{
                display: "flex",
                gap: toRem(16),
                overflowX: "auto",
                scrollSnapType: "x mandatory",
                WebkitOverflowScrolling: "touch",
                paddingBottom: 0,
                scrollbarWidth: "none",
                msOverflowStyle: "none",
              }}
            >
              {normalizedHotels.map((hotel: any, idx: number) => {
                const id = getHotelKey(hotel, idx)
                return (
                  <div
                    key={id}
                    className="landing-hotel__item"
                    style={{
                      flex: `0 0 ${toRem(230)}`,
                      scrollSnapAlign: "start",
                    }}
                  >
                    <Popover
                      trigger={["hover", "click"]}
                      mouseEnterDelay={0.1}
                      overlayClassName="landing-hotel__popover"
                      placement="bottom"
                      content={<HotelCard hotel={hotel} mode="expanded" />}
                      onOpenChange={(open) => handlePopoverOpenChange(id, open)}
                    >
                      <div className="landing-hotel__trigger" style={{ cursor: "pointer" }}>
                        <HotelCard hotel={hotel} mode="compact" />
                      </div>
                    </Popover>
                  </div>
                )
              })}
            </div>

            <style jsx global>{`
              .landing-hotel__slider {
                position: relative;
                padding: 0 3.5rem;
                overflow: visible;
              }
              .landing-hotel__nav {
                position: absolute;
                top: 50%;
                transform: translateY(-50%);
                z-index: 5;
                width: 4.4rem;
                height: 4.4rem;
                display: flex;
                align-items: center;
                justify-content: center;
                box-shadow: 0 0.9rem 2.2rem rgba(0, 0, 0, 0.15);
                background: rgba(255, 255, 255, 0.95);
                border: 0.1rem solid rgba(0, 0, 0, 0.08);
              }
              .landing-hotel__nav--prev {
                left: 0.5rem;
              }
              .landing-hotel__nav--next {
                right: 0.5rem;
              }
              @media (max-width: 575px) {
                .landing-hotel__slider {
                  padding: 0 3.5rem;
                }
                .landing-hotel__nav--prev {
                  left: 0.4rem;
                }
                .landing-hotel__nav--next {
                  right: 0.4rem;
                }
              }

              .landing-hotel__item {
                height: 39.1rem;
              }
              .landing-hotel__trigger {
                height: 100%;
              }
              .landing-hotel__card {
                height: 100%;
                display: flex;
                flex-direction: column;
                overflow: hidden;
              }
              .landing-hotel__card--expanded {
                width: 37.8rem;
                height: auto;
              }
              .landing-hotel__card .ant-card-body {
                display: flex;
                flex-direction: column;
                flex: 1;
              }
              .landing-hotel__media {
                flex: 0 0 auto;
                height: 15.6rem;
              }
              .landing-hotel__media--expanded {
                height: 20.0rem;
              }
              .landing-hotel__content {
                display: flex;
                flex-direction: column;
                flex: 1;
                min-height: 0;
                overflow: hidden;
              }
              .landing-hotel__content--expanded {
                min-height: unset;
              }
              .landing-hotel__name {
                font-size: 1.6rem;
                font-weight: 600;
                margin: 0 0 0.9rem;
                display: -webkit-box;
                -webkit-line-clamp: 2;
                -webkit-box-orient: vertical;
                overflow: hidden;
                min-height: 4.4rem;
              }
              .landing-hotel__name--expanded {
                -webkit-line-clamp: unset;
                display: block;
                overflow: visible;
                min-height: unset;
              }
              .landing-hotel__meta {
                margin-bottom: 0.9rem;
                min-height: 4.2rem;
                overflow: hidden;
              }
              .landing-hotel__meta--expanded {
                min-height: unset;
              }
              .landing-hotel__reviews {
                display: -webkit-box;
                -webkit-line-clamp: 1;
                -webkit-box-orient: vertical;
                overflow: hidden;
              }
              .landing-hotel__reviews--expanded {
                display: block;
                overflow: visible;
              }
              .landing-hotel__price {
                margin-top: auto;
                display: block;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
                font-size: 1.6rem;
              }
              .landing-hotel__track::-webkit-scrollbar {
                width: 0;
                height: 0;
              }
              .landing-hotel__track::-webkit-scrollbar-thumb {
                background: transparent;
              }
              .landing-hotel__track::-webkit-scrollbar-track {
                background: transparent;
              }
              .landing-hotel__popover .ant-popover-inner {
                padding: 0;
              }
              .landing-hotel__popover .ant-popover-inner-content {
                padding: 0;
              }
            `}</style>
          </div>
        ) : (
          <Text>Đang tải thông tin khách sạn...</Text>
        )}
      </Col>
    </Row>
  )
}

export default LandingHotel
